// One rule for an index's printed name — the website's, used by every board on
// raisedbeef.com/index and, from 2026-10-15, by the app (owner: "Our index
// names on the index page need to match the naming convention on our
// webpage"). Before this the app carried its own list ("Feeder Steers",
// "Calves", "Cows", "Bulls") that matched nothing the website prints.
//
// The website's two boards:
//   seven-day quote board  →  <code> <short name>      DVAFS Feeder Steer
//   daily live board       →  Daily Live <short name>  Daily Live Feeder Steer
// where the short name is the definition's name with "DVA Real Time " and
// " Index" taken off. The index page's own title keeps the whole name.

/** "Feeder Steer" from "DVA Real Time Feeder Steer Index"; "7WT Steer" from "DVA Real Time 7WT Steer Index". */
export function indexShortName(name: string): string {
  return name.replace(/^(RB|DVA) (Real Time )?/, "").replace(/ Index$/, "");
}

/**
 * The definitions as public.rb_index_definitions holds them (every row,
 * verified 2026-10-15). Only a fallback: a row the readers return carries its
 * own code and name, and those win. The live map (rb_index_live) carries
 * neither, which is the one place this is read.
 */
export const INDEX_DEFINITIONS: Record<string, { code: string; name: string }> = {
  rb_feeder_steer: { code: "DVAFS", name: "DVA Real Time Feeder Steer Index" },
  rb_feeder_heifer: { code: "DVAFH", name: "DVA Real Time Feeder Heifer Index" },
  rb_calf: { code: "DVACF", name: "DVA Real Time Calf Index" },
  rb_cow: { code: "DVACW", name: "DVA Real Time Cow Index" },
  rb_slaughter_bull: { code: "DVABL", name: "DVA Real Time Slaughter Bull Index" },
  rb_steer_heifer_500: { code: "DVASH", name: "DVA Real Time Steer & Heifer 500+ Index" },
  rb_feeder_cattle: { code: "DVAFC", name: "DVA Feeder Cattle Index" },
  rb_3wt_steers: { code: "DVA3S", name: "DVA Real Time 3WT Steer Index" },
  rb_3wt_heifers: { code: "DVA3H", name: "DVA Real Time 3WT Heifer Index" },
  rb_4wt_steers: { code: "DVA4S", name: "DVA Real Time 4WT Steer Index" },
  rb_4wt_heifers: { code: "DVA4H", name: "DVA Real Time 4WT Heifer Index" },
  rb_5wt_steers: { code: "DVA5S", name: "DVA Real Time 5WT Steer Index" },
  rb_5wt_heifers: { code: "DVA5H", name: "DVA Real Time 5WT Heifer Index" },
  rb_6wt_steers: { code: "DVA6S", name: "DVA Real Time 6WT Steer Index" },
  rb_6wt_heifers: { code: "DVA6H", name: "DVA Real Time 6WT Heifer Index" },
  rb_7wt_steers: { code: "DVA7S", name: "DVA Real Time 7WT Steer Index" },
  rb_7wt_heifers: { code: "DVA7H", name: "DVA Real Time 7WT Heifer Index" },
  rb_8wt_steers: { code: "DVA8S", name: "DVA Real Time 8WT Steer Index" },
  rb_8wt_heifers: { code: "DVA8H", name: "DVA Real Time 8WT Heifer Index" },
  rb_9wt_steers: { code: "DVA9S", name: "DVA Real Time 9WT Steer Index" },
  rb_9wt_heifers: { code: "DVA9H", name: "DVA Real Time 9WT Heifer Index" },
  rb_10wt_steers: { code: "DVATS", name: "DVA Real Time 10WT Steer Index" },
  rb_10wt_heifers: { code: "DVATH", name: "DVA Real Time 10WT Heifer Index" },
};

/** The short name for a slug: the row's own name when it has one, else the definition's. */
export function indexNameFor(slug: string, name?: string | null): string {
  const n = name && name.trim() ? name : INDEX_DEFINITIONS[slug]?.name ?? slug;
  return indexShortName(n);
}

/** The code for a slug: the row's own when it has one (a daily "-D" suffix dropped), else the definition's. */
export function indexCodeFor(slug: string, code?: string | null): string {
  const c = code && code.trim() ? code.replace(/-D$/, "") : INDEX_DEFINITIONS[slug]?.code ?? "";
  return c;
}
